import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

type User = {
  name: string;
  email: string;
  role: string;
};

type AuthContextType = {
  isAuthenticated: boolean;
  user: User | null;
  login: (userData?: User) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check localStorage on app start
  useEffect(() => {
    const checkStoredAuth = () => {
      try {
        const storedAuth = localStorage.getItem('wellbnb_auth');
        const storedUser = localStorage.getItem('wellbnb_user');
        
        if (storedAuth === 'true' && storedUser) {
          const userData = JSON.parse(storedUser);
          setIsAuthenticated(true);
          setUser(userData);
        }
      } catch (error) {
        console.error('Error checking stored auth:', error);
        // Clear invalid data
        localStorage.removeItem('wellbnb_auth');
        localStorage.removeItem('wellbnb_user');
      } finally {
        setIsLoading(false);
      }
    };

    checkStoredAuth();
  }, []);

  const login = (userData?: User) => {
    setIsAuthenticated(true);
    
    // Set default user data if none provided
    const defaultUser: User = {
      name: userData?.name || "WellBnB User",
      email: userData?.email || "user@wellbnb.com", 
      role: userData?.role || "Member"
    };
    
    setUser(userData || defaultUser);
    
    // Persist to localStorage
    localStorage.setItem('wellbnb_auth', 'true');
    localStorage.setItem('wellbnb_user', JSON.stringify(userData || defaultUser));
  };

  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
    
    // Clear localStorage
    localStorage.removeItem('wellbnb_auth');
    localStorage.removeItem('wellbnb_user');
  };

  // Don't render children until we've checked localStorage
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#251529',
        color: 'white',
        fontFamily: 'Inter, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid rgba(168, 111, 214, 0.3)',
            borderTop: '3px solid #a86fd6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p>Loading WellBnB...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
